import React from 'react';
import { Brain, Activity, TrendingUp, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Dashboard: React.FC = () => {
    const { user } = useAuth();

    return (
        <div className="p-8 max-w-7xl mx-auto">
            <div className="mb-8">
                <h1 className="text-4xl font-bold text-gray-900 mb-2">Magnus Resolve</h1>
                <p className="text-lg text-gray-600">
                    AI-Powered Failure Analysis & Resolution Platform
                </p>
            </div>

            {/* Welcome Message */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white mb-8">
                <h2 className="text-2xl font-bold mb-2">
                    Welcome, {user?.displayName || user?.email}!
                </h2>
                <p className="text-blue-100">
                    Your advanced AI-powered failure analysis platform is ready.
                    Start detecting, analyzing, and resolving failures with next-generation intelligence.
                </p>
            </div>

            {/* Feature Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <Brain className="w-8 h-8 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                            COMING SOON
                        </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">AI Defect Detection</h3>
                    <p className="text-sm text-gray-600">
                        Leverage advanced AI models to automatically detect and classify defects in real-time
                    </p>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <Activity className="w-8 h-8 text-green-600" />
                        <span className="text-sm font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full">
                            COMING SOON
                        </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Root Cause Analysis</h3>
                    <p className="text-sm text-gray-600">
                        Deep learning algorithms identify true root causes, accelerating resolution
                    </p>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <TrendingUp className="w-8 h-8 text-purple-600" />
                        <span className="text-sm font-semibold text-purple-600 bg-purple-50 px-3 py-1 rounded-full">
                            COMING SOON
                        </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Trend Analysis</h3>
                    <p className="text-sm text-gray-600">
                        Predictive analytics reveal patterns before they become critical failures
                    </p>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <AlertCircle className="w-8 h-8 text-amber-600" />
                        <span className="text-sm font-semibold text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                            COMING SOON
                        </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Knowledge Base</h3>
                    <p className="text-sm text-gray-600">
                        Access historical failure data and industry best practices instantly
                    </p>
                </div>
            </div>

            {/* Development Status */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-blue-900 mb-4">🚧 Under Development</h3>
                <div className="space-y-3 text-sm text-blue-800">
                    <p>
                        <strong>Magnus Resolve</strong> is currently in active development. This standalone app shares
                        the same secure authentication and RBAC system as Magnus Core (DFMEA) and Magnus PFMEA.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                        <div>
                            <h4 className="font-semibold mb-2">✅ Completed:</h4>
                            <ul className="list-disc list-inside space-y-1 text-blue-700">
                                <li>Firebase Authentication Integration</li>
                                <li>Shared RBAC with Magnus Core</li>
                                <li>Project Structure & Build System</li>
                                <li>Development Environment Setup</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-2">🚀 Advanced Features (Coming Soon):</h4>
                            <ul className="list-disc list-inside space-y-1 text-blue-700">
                                <li>FMEA from Field AI Sentinel</li>
                                <li>AI-Drafted 8D Sections</li>
                                <li>Design-Change Impact Predictor</li>
                                <li>Automated Reliability Block Diagrams</li>
                                <li>AI-Powered Root Cause Ranking</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
